/**
 * The layout popover: a title row and a grid of region tiles, opened under
 * the app bar's `Layout` button.
 *
 * A plain positioned `div` rather than `punica-menu`: the component clones
 * its children when it opens and keeps only `<punica-menu-item>` nodes, so a
 * grid of pressed/unpressed tiles cannot live inside it (see
 * `../appBar/index.ts` `#openMenu` for the same finding). The popover owns
 * its dismissal — an outside press, Escape, a resize — and re-reads every
 * tile's state from the caller on each open and each refresh, so it never
 * holds a copy of what is visible.
 */

export interface LayoutTile {
  id: string;
  /** The tile's tooltip and accessible name; nothing is drawn under the glyph. */
  label: string;
  /** Font Awesome classes for the glyph. */
  icon: string;
  /** Whether the region is on screen now. */
  pressed(): boolean;
  /** Flip the region. The popover refreshes itself afterwards. */
  toggle(): void;
}

export interface LayoutPopoverOptions {
  /** Accessible name of the card (not drawn). */
  title: string;
  tiles: () => LayoutTile[];
  /** Class prefix; the two shells share the shape and differ in prefix. */
  className?: string;
}

export interface LayoutPopoverHandle {
  el: HTMLElement;
  open(anchor: HTMLElement): void;
  close(): void;
  toggleOpen(anchor: HTMLElement): void;
  isOpen(): boolean;
  /** Re-read every tile's pressed state (no-op while closed). */
  refresh(): void;
  dispose(): void;
}

const GAP_BELOW_ANCHOR = 6;
const VIEWPORT_MARGIN = 8;

export function createLayoutPopover(
  options: LayoutPopoverOptions
): LayoutPopoverHandle {
  const prefix = options.className ?? 'layout-popover';
  const root = document.createElement('div');
  root.className = prefix;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-label', options.title);
  root.hidden = true;

  // No title row and no captions: the card is glyphs only, each glyph
  // named by its tooltip — asked for after the first sighting.
  const grid = document.createElement('div');
  grid.className = `${prefix}__grid`;
  root.appendChild(grid);

  let anchorEl: HTMLElement | null = null;
  const buttons = new Map<string, HTMLButtonElement>();
  let frame = 0;

  function render(): void {
    grid.innerHTML = '';
    buttons.clear();
    for (const tile of options.tiles()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `${prefix}__tile`;
      button.dataset.region = tile.id;
      button.title = tile.label;
      const glyph = document.createElement('i');
      glyph.className = tile.icon;
      glyph.setAttribute('aria-hidden', 'true');
      button.setAttribute('aria-label', tile.label);
      button.appendChild(glyph);
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        tile.toggle();
        refresh();
      });
      buttons.set(tile.id, button);
      grid.appendChild(button);
    }
    refresh();
  }

  function refresh(): void {
    if (root.hidden) return;
    for (const tile of options.tiles()) {
      const button = buttons.get(tile.id);
      if (!button) continue;
      const on = tile.pressed();
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  function place(): void {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const width = root.offsetWidth || 0;
    const viewportWidth =
      typeof window !== 'undefined' ? window.innerWidth : rect.right;
    // Right edge aligned with the anchor's right edge, kept on screen.
    let left = rect.right - width;
    if (left + width > viewportWidth - VIEWPORT_MARGIN) {
      left = viewportWidth - VIEWPORT_MARGIN - width;
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    root.style.top = `${Math.round(rect.bottom + GAP_BELOW_ANCHOR)}px`;
    root.style.left = `${Math.round(left)}px`;
  }

  function onPointerDown(event: Event): void {
    const target = event.target as Node | null;
    if (!target) return;
    if (root.contains(target)) return;
    if (anchorEl && anchorEl.contains(target)) return;
    close();
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  }

  /**
   * Keep the card under its anchor for as long as it is open. The anchor
   * sits in a split pane that applies its sizes on its own frame, so a rect
   * read at the click can be the pane's old geometry and the card is then
   * left where the button was, not where it ends up — sighted in the agent
   * window on the first open after a reload, with the side pane's collapse
   * still unapplied. One rect read per frame while open, nothing while
   * closed.
   */
  function follow(): void {
    place();
    frame = requestAnimationFrame(follow);
  }

  function open(anchor: HTMLElement): void {
    anchorEl = anchor;
    if (!root.isConnected) document.body.appendChild(root);
    root.hidden = false;
    render();
    place();
    if (typeof requestAnimationFrame === 'function') {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(follow);
    }
    anchor.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', place);
    window.addEventListener('blur', close);
  }

  function close(): void {
    if (root.hidden) return;
    root.hidden = true;
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    anchorEl?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('resize', place);
    window.removeEventListener('blur', close);
  }

  return {
    el: root,
    open,
    close,
    toggleOpen: (anchor) => {
      if (!root.hidden && anchorEl === anchor) close();
      else open(anchor);
    },
    isOpen: () => !root.hidden,
    refresh,
    dispose: () => {
      close();
      root.remove();
    }
  };
}
